/**
 * RTWO Blockchain Protocol Engine — Domain Models
 * Phase 3 | Version: 1.0 Protocol Edition
 *
 * Strongly typed domain models for the RTWO blockchain protocol layer.
 * These models define the internal protocol structure independent of
 * consensus, networking, and mainnet deployment.
 *
 * Truthful Data Policy: All runtime values are null until real
 * infrastructure is connected. No fake data is permitted.
 */

// ─────────────────────────────────────────────
// PROTOCOL PRIMITIVES
// ─────────────────────────────────────────────

export type ProtocolHash = string;
export type ProtocolAddress = string;
export type ProtocolBlockNumber = bigint | null;
export type ProtocolTimestamp = number | null;
export type ProtocolSignature = string;
export type ProtocolNonce = bigint | null;
export type ProtocolAmount = string; // string to preserve precision
export type ProtocolFee = string;
export type ProtocolVersion = string;

// ─────────────────────────────────────────────
// BLOCK STATUS & LIFECYCLE
// ─────────────────────────────────────────────

/**
 * Block Lifecycle:
 * Proposed → Validating → Accepted → Finalized → Archived → Rejected
 */
export type BlockStatus =
  'proposed' | 'validating' | 'accepted' | 'finalized' | 'archived' | 'rejected';

export type BlockLifecycleStage = {
  stage: BlockStatus;
  label: string;
  description: string;
  terminal: boolean;
};

export const BLOCK_LIFECYCLE: BlockLifecycleStage[] = [
  {
    stage: 'proposed',
    label: 'Proposed',
    description: 'Block has been proposed by a validator and submitted to the protocol engine.',
    terminal: false,
  },
  {
    stage: 'validating',
    label: 'Validating',
    description:
      'Block is undergoing structural validation, transaction root verification, and signature checks.',
    terminal: false,
  },
  {
    stage: 'accepted',
    label: 'Accepted',
    description: 'Block has passed all validation checks and is accepted into the chain.',
    terminal: false,
  },
  {
    stage: 'finalized',
    label: 'Finalized',
    description: 'Block is irreversibly committed to the canonical chain.',
    terminal: false,
  },
  {
    stage: 'archived',
    label: 'Archived',
    description: 'Block has been archived after finalization for historical access.',
    terminal: true,
  },
  {
    stage: 'rejected',
    label: 'Rejected',
    description: 'Block failed validation and was rejected from the chain.',
    terminal: true,
  },
];

// ─────────────────────────────────────────────
// TRANSACTION STATUS & LIFECYCLE
// ─────────────────────────────────────────────

/**
 * Transaction Lifecycle:
 * Created → Pending → Validated → Accepted → Included in Block → Finalized → Failed
 */
export type TransactionStatus =
  'created' | 'pending' | 'validated' | 'accepted' | 'included' | 'finalized' | 'failed';

export type TransactionLifecycleStage = {
  stage: TransactionStatus;
  label: string;
  description: string;
  terminal: boolean;
};

export const TRANSACTION_LIFECYCLE: TransactionLifecycleStage[] = [
  {
    stage: 'created',
    label: 'Created',
    description: 'Transaction has been constructed and signed by the sender.',
    terminal: false,
  },
  {
    stage: 'pending',
    label: 'Pending',
    description: 'Transaction has been submitted to the mempool and is awaiting validation.',
    terminal: false,
  },
  {
    stage: 'validated',
    label: 'Validated',
    description: 'Transaction has passed signature, nonce, and fee validation checks.',
    terminal: false,
  },
  {
    stage: 'accepted',
    label: 'Accepted',
    description: 'Transaction has been accepted into the ready queue for block inclusion.',
    terminal: false,
  },
  {
    stage: 'included',
    label: 'Included in Block',
    description: 'Transaction has been included in a proposed block.',
    terminal: false,
  },
  {
    stage: 'finalized',
    label: 'Finalized',
    description: 'Transaction is irreversibly committed as part of a finalized block.',
    terminal: true,
  },
  {
    stage: 'failed',
    label: 'Failed',
    description: 'Transaction failed validation or execution and was rejected.',
    terminal: true,
  },
];

// ─────────────────────────────────────────────
// ACCOUNT TYPE
// ─────────────────────────────────────────────

export type AccountType = 'standard' | 'validator' | 'contract' | 'system' | 'multisig';

// ─────────────────────────────────────────────
// RECEIPT EXECUTION STATUS
// ─────────────────────────────────────────────

export type ReceiptExecutionStatus =
  'success' | 'reverted' | 'out_of_fee' | 'invalid_nonce' | 'invalid_signature';

// ─────────────────────────────────────────────
// DOMAIN MODEL 1: RTWOBlock
// ─────────────────────────────────────────────

export interface RTWOBlock {
  /** Sequential block number in the canonical chain */
  blockNumber: ProtocolBlockNumber;

  /** Cryptographic hash of this block */
  blockHash: ProtocolHash | null;

  /** Hash of the preceding block */
  parentHash: ProtocolHash | null;

  /** Unix timestamp when the block was proposed */
  timestamp: ProtocolTimestamp;

  /** Protocol version at time of block creation */
  version: ProtocolVersion;

  /** Merkle root of the world state after this block */
  stateRoot: ProtocolHash | null;

  /** Merkle root of all transactions in this block */
  transactionRoot: ProtocolHash | null;

  /** Merkle root of all transaction receipts */
  receiptRoot: ProtocolHash | null;

  /** Address of the validator that proposed this block */
  proposer: ProtocolAddress | null;

  /** Number of transactions included in this block */
  transactionCount: number | null;

  /** Total size of the block in bytes */
  blockSize: number | null;

  /** Current lifecycle status of the block */
  status: BlockStatus;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 2: RTWOTransaction
// ─────────────────────────────────────────────

export interface RTWOTransaction {
  /** Unique cryptographic hash identifying this transaction */
  transactionHash: ProtocolHash | null;

  /** Address of the account sending this transaction */
  sender: ProtocolAddress | null;

  /** Address of the account receiving this transaction */
  receiver: ProtocolAddress | null;

  /** Amount of native currency transferred */
  amount: ProtocolAmount | null;

  /** Transaction fee paid to the protocol */
  fee: ProtocolFee | null;

  /** Sender's account nonce at time of transaction creation */
  nonce: ProtocolNonce;

  /** Cryptographic signature authorizing the transaction */
  signature: ProtocolSignature | null;

  /** Unix timestamp when the transaction was created */
  timestamp: ProtocolTimestamp;

  /** Current lifecycle status of the transaction */
  status: TransactionStatus;

  /** Reference to the block that includes this transaction (null until included) */
  blockReference: ProtocolBlockNumber;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 3: RTWOAccount
// ─────────────────────────────────────────────

export interface RTWOAccount {
  /** Unique protocol address identifying this account */
  address: ProtocolAddress | null;

  /** Current native currency balance */
  balance: ProtocolAmount | null;

  /** Current transaction nonce (number of transactions sent) */
  nonce: ProtocolNonce;

  /** Classification of the account within the protocol */
  accountType: AccountType;

  /** Unix timestamp when the account was first created on-chain */
  createdAt: ProtocolTimestamp;

  /** Unix timestamp of the last state update to this account */
  updatedAt: ProtocolTimestamp;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 4: RTWOState
// ─────────────────────────────────────────────

export interface RTWOState {
  /** Merkle root representing the entire world state */
  stateRoot: ProtocolHash | null;

  /** Total number of accounts in the state trie */
  accountCount: number | null;

  /** Total supply of native currency in existence */
  totalSupply: ProtocolAmount | null;

  /** Amount of native currency locked in protocol contracts */
  lockedSupply: ProtocolAmount | null;

  /** Amount of native currency staked by validators */
  stakedSupply: ProtocolAmount | null;

  /** Block number at which this state snapshot was taken */
  lastUpdatedBlock: ProtocolBlockNumber;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 5: RTWOReceipt
// ─────────────────────────────────────────────

export interface ProtocolEventLog {
  /** Address of the contract that emitted this event */
  emitter: ProtocolAddress | null;
  /** Event topic identifiers */
  topics: ProtocolHash[];
  /** ABI-encoded event data */
  data: string | null;
  /** Index of this log within the transaction */
  logIndex: number;
}

export interface RTWOReceipt {
  /** Hash of the transaction this receipt corresponds to */
  transactionHash: ProtocolHash | null;

  /** Execution result of the transaction */
  executionStatus: ReceiptExecutionStatus;

  /** Amount of fee consumed during execution */
  feeUsed: ProtocolFee | null;

  /** Ordered list of event logs emitted during execution */
  eventLogs: ProtocolEventLog[];

  /** Unix timestamp when the receipt was generated */
  timestamp: ProtocolTimestamp;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 6: RTWOMempool
// ─────────────────────────────────────────────

export interface RTWOMempool {
  /** All transactions currently in the mempool */
  pendingTransactions: RTWOTransaction[];

  /** Total number of transactions in the mempool */
  queueSize: number;

  /** Transactions undergoing validation checks */
  validationQueue: RTWOTransaction[];

  /** Transactions that have passed validation and are ready for block inclusion */
  readyQueue: RTWOTransaction[];

  /** Transactions that failed validation and were rejected */
  rejectedQueue: RTWOTransaction[];
}

// ─────────────────────────────────────────────
// PROTOCOL VERSION DESCRIPTOR
// ─────────────────────────────────────────────

export interface ProtocolVersionDescriptor {
  version: ProtocolVersion;
  name: string;
  releaseDate: string | null;
  status: 'development' | 'testnet' | 'mainnet' | 'deprecated';
  features: string[];
}

export const PROTOCOL_VERSION: ProtocolVersionDescriptor = {
  version: '1.0.0-phaseB3',
  name: 'RTWO Protocol Engine v1',
  releaseDate: null,
  status: 'development',
  features: [
    'Block Creation & Validation',
    'Transaction Lifecycle Management',
    'Fee Calculation Engine',
    'Nonce Validation',
    'Account State Management',
    'Receipt Generation',
    'Mempool Management',
    'State Transition Engine',
    'RPC Provider Runtime Bridge',
  ],
};

export const PROTOCOL_ENGINE_VERSION = {
  moduleId: 'RTWO-PROT-001',
  moduleName: 'Blockchain Protocol Engine',
  version: '1.0.0-phaseB3',
  phase: 'Phase B.3 — Protocol RPC Runtime Activation',
  status: 'RPC Provider Connected — Chain Data Pending Ledger and Storage Ingestion',
  productionReadiness: 'Runtime Protocol-RPC Bridge Active — No Fabricated Chain State',
  policy: 'Truthful Data Policy Enforced — No Fake Blocks, Transactions, or Accounts',
} as const;

// ─────────────────────────────────────────────
// EMPTY STATE FACTORIES (Truthful Data Policy)
// ─────────────────────────────────────────────

export function createEmptyBlock(): RTWOBlock {
  return {
    blockNumber: null,
    blockHash: null,
    parentHash: null,
    timestamp: null,
    version: PROTOCOL_VERSION.version,
    stateRoot: null,
    transactionRoot: null,
    receiptRoot: null,
    proposer: null,
    transactionCount: null,
    blockSize: null,
    status: 'proposed',
  };
}

export function createEmptyTransaction(): RTWOTransaction {
  return {
    transactionHash: null,
    sender: null,
    receiver: null,
    amount: null,
    fee: null,
    nonce: null,
    signature: null,
    timestamp: null,
    status: 'created',
    blockReference: null,
  };
}

export function createEmptyAccount(): RTWOAccount {
  return {
    address: null,
    balance: null,
    nonce: null,
    accountType: 'standard',
    createdAt: null,
    updatedAt: null,
  };
}

export function createEmptyState(): RTWOState {
  return {
    stateRoot: null,
    accountCount: null,
    totalSupply: null,
    lockedSupply: null,
    stakedSupply: null,
    lastUpdatedBlock: null,
  };
}

export function createEmptyReceipt(): RTWOReceipt {
  return {
    transactionHash: null,
    executionStatus: 'success',
    feeUsed: null,
    eventLogs: [],
    timestamp: null,
  };
}

export function createEmptyMempool(): RTWOMempool {
  return {
    pendingTransactions: [],
    queueSize: 0,
    validationQueue: [],
    readyQueue: [],
    rejectedQueue: [],
  };
}
